/**
 * A type representing a path to a property within an object,
 * represented as a tuple of keys.
 */
export type PropertyPath<T> = {
  [K in keyof T]: T[K] extends object ? [K] | [K, ...PropertyPath<T[K]>] : [K];
}[keyof T];

/**
 * A type representing the value of a property at a given path within an object.
 */
export type PropertyAtPath<T, P extends PropertyPath<T>> = P extends [infer K]
  ? K extends keyof T
    ? T[K]
    : never
  : P extends [infer K, ...infer R]
    ? K extends keyof T
      ? R extends PropertyPath<T[K]>
        ? PropertyAtPath<T[K], R>
        : never
      : never
    : never;

export function isArray<T>(value: T | readonly T[]): value is readonly T[] {
  return Array.isArray(value);
}

export function concat<T>(array1: T[], array2: T[]): T[] {
  return array1.concat(array2);
}

export function copy(array: null): null;
export function copy<A extends readonly unknown[]>(array: A): A;
export function copy<A extends readonly unknown[]>(array: A | null): A | null {
  if (array) {
    return [...array] as unknown as A;
  } else {
    return null;
  }
}

export function getProperty(
  obj: object | null | undefined,
  prop: string | readonly (string | number)[],
): unknown {
  if (!obj) return null;
  if (isArray(prop)) {
    const propArr = copy(prop);
    let current: unknown = obj;
    for (const p of propArr) {
      if (!current || typeof current !== 'object') return null;
      current = (current as Record<string | number, unknown>)[p];
    }
    return current;
  } else return (obj as Record<string, unknown>)[prop];
}

/**
 * Fill `array` with `value`. When `count` is supplied, fills the first
 * `count` slots; otherwise fills `array.length` slots. Allocates a new
 * array if `array` is null. Mirrors the legacy `ArrayUtil.fill` global.
 */
export function fill<T>(array: T[] | null, value: T, count = 0): T[] {
  const target = array ?? [];
  const len = count === 0 ? target.length : count;
  for (let i = 0; i < len; i++) {
    target[i] = value;
  }
  return target;
}

/**
 * Remove the first occurrence of `item` from `array` in place, returning
 * `item`. No-op if not found.
 */
export function remove<T>(array: T[], item: T): T {
  const index = array.indexOf(item);
  if (index >= 0) array.splice(index, 1);
  return item;
}

/**
 * Test whether `array` contains `element` (by `==` equality, matching the
 * legacy `ArrayUtil.contains` semantics).
 */
export function contains<T>(array: readonly T[], element: T): boolean {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i] == element) return true;
  }
  return false;
}

/**
 * Deep-clone an array (1-level recursive over nested arrays). Mirrors the
 * legacy `ArrayUtil.clone` global.
 */
export function clone<T>(array: T): T {
  if (Array.isArray(array)) {
    return array.map((item: unknown) => clone(item)) as unknown as T;
  }
  return array;
}

/**
 * Insert an item at a given index in place, returning the mutated array.
 * Matches ArrayUtil.insert() from @suntex.classic/utils.
 */
export function insert<T>(array: T[], index: number, item: T): T[] {
  array.splice(index, 0, item);
  return array;
}
